import React, { useEffect } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
// import { ColorModeSwitcher } from "./ColorModeSwitcher"
import Routes from './Router';
import theme from './styles/theme';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, ConnectedProps } from 'react-redux';
import { useFlag } from '@unleash/proxy-client-react';

import {
  initialize,
  validateAuthToken,
  validateEntrataAuthCode,
} from './actions/auth';
import Notification from './components/UI/Notification';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

import {
  bootIntercom,
  shutdownIntercom,
  updateIntercom,
} from './utils/intercom';
import { StoreState } from './types/state';

function App(props: AppProps) {
  let { initialize, validateAuthToken, validateEntrataAuthCode, user } = props;
  const location = useLocation();
  const isIntercomEnabled = useFlag('intercom');

  useEffect(() => {
    if (isIntercomEnabled) {
      if (user.isAuthenticated) {
        bootIntercom(user);
      } else {
        shutdownIntercom();
      }
    }
  }, [user, isIntercomEnabled]);

  useEffect(() => {
    if (isIntercomEnabled && user.isAuthenticated) {
      updateIntercom();
    }
  }, [location, isIntercomEnabled, user.isAuthenticated]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isReferedByEntrata = urlParams.get('referrer') === 'entrata';
    const authCode = urlParams.get('auth_code');

    const tokenChecker = setInterval(() => {
      validateAuthToken();
    }, 5 * 60 * 1000);

    if (isReferedByEntrata && authCode) {
      validateEntrataAuthCode(authCode);
    } else if (initialize) initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps

    return () => {
      clearInterval(tokenChecker);
    };
  }, [initialize, validateAuthToken, validateEntrataAuthCode, location.search]);

  let cspString = `default-src 'self'; script-src 'self' https://fonts.googleapis.com https://d2twymdrqimvrw.cloudfront.net https://widget.intercom.io https://js.intercomcdn.com; style-src 'unsafe-inline' https://fonts.googleapis.com https://d2twymdrqimvrw.cloudfront.net; img-src 'self' data: https://fonts.googleapis.com https://d2twymdrqimvrw.cloudfront.net https://static.intercomassets.com https://js.intercomcdn.com; connect-src https://*.beekin.co https://*.sentry.io https://*.amazonaws.com https://api-js.mixpanel.com https://api.intercom.io https://api-iam.intercom.io https://*.intercom.io wss://*.intercom.io wss://*.intercom-messenger.com; frame-src https://intercom.com https://intercomcdn.com; font-src 'self' https://fonts.gstatic.com https://fonts.intercomcdn.com; media-src 'self' https://js.intercomcdn.com;`;

  if (process.env.NODE_ENV === 'development') {
    cspString = `default-src 'self'; script-src 'self' 'unsafe-inline' https://fonts.googleapis.com https://d2twymdrqimvrw.cloudfront.net https://widget.intercom.io https://js.intercomcdn.com; style-src 'unsafe-inline' https://fonts.googleapis.com https://d2twymdrqimvrw.cloudfront.net; img-src 'self' data: https://fonts.googleapis.com https://d2twymdrqimvrw.cloudfront.net https://static.intercomassets.com https://js.intercomcdn.com; connect-src http://localhost:* https://*.beekin.co https://*.sentry.io https://*.amazonaws.com https://api-js.mixpanel.com https://api.intercom.io https://api-iam.intercom.io https://*.intercom.io wss://*.intercom.io wss://*.intercom-messenger.com; frame-src https://intercom.com https://intercomcdn.com; font-src 'self' https://fonts.gstatic.com https://fonts.intercomcdn.com; media-src 'self' https://js.intercomcdn.com;`;
  }

  return (
    <ChakraProvider theme={theme}>
      <HelmetProvider>
        <Routes />
        <Notification />
        <Helmet>
          <meta charSet="utf-8" />
          <meta http-equiv="Content-Security-Policy" content={cspString}></meta>
          <meta http-equiv="X-Content-Type-Options" content="nosniff"></meta>
          <meta http-equiv="Referrer-Policy" content="no-referrer"></meta>
          <meta
            http-equiv="Feature-Policy"
            content="microphone none; geolocation none; camera none;"
          ></meta>
        </Helmet>
      </HelmetProvider>
    </ChakraProvider>
  );
}

const mapStateToProps = (state: StoreState) => ({
  user: state.auth,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      initialize,
      validateAuthToken,
      validateEntrataAuthCode,
    },
    dispatch
  );

const connector = connect(mapStateToProps, mapDispatchToProps);

type AppProps = ConnectedProps<typeof connector>;

export default connector(App);
